// Siparis kalemi: baskiya gidecek tasarim dosyalari (acik/koyu zemin) ve
// tedarikci (Rexven) urunu. Acik/koyu ikinci tasarim Design.metadata'dan gelir;
// kumas rengine gore hangisinin basilacagi isaretlenir.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, Palette, ExternalLink, Factory, Star } from "lucide-react";
import { clsx } from "clsx";
import { getBackendUrl } from "../../lib/http";
import { garmentHex } from "@erc/shared";
import { downloadPrintFile } from "../../lib/print-file";
import { OrderSampleMockup } from "./OrderSampleMockup";

export interface LinkedDesign {
  id: string;
  name: string;
  sku: string | null;
  thumbnailUrl: string | null;
  originalUrl: string | null;
  editedUrl: string | null;
  theme?: "light" | "dark" | null;
  variantUrl?: string | null;
  variantTheme?: "light" | "dark" | null;
  /** Custom Liste Studyosu tasarimi: siparis dosyasi sablondan basilir */
  customListingId?: string | null;
}

export interface SupplierInfo {
  supplier: string;
  productName: string | null;
  sku: string | null;
  url: string | null;
  size: string | null;
  variantId: string | null;
}

const absUrl = (u?: string | null) =>
  !u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${getBackendUrl()}${u}`;

// Kumas rengi -> zemin. Yalnizca emin olunan adlar; bilinmeyen renkte oneri yok.
const DARK = /\b(black|pepper|navy|charcoal|graphite|espresso|chocolate|brown|maroon|burgundy|forest|hunter|military|dark|midnight|true navy|blue jean|denim|heather(ed)? (black|navy|charcoal)|siyah|lacivert|antrasit|bordo|kahve)\b/i;
const LIGHT = /\b(white|ivory|natural|cream|sand|bone|banana|butter|light|chalky|blossom|orchid|pink|yellow|mint|beige|ash|heather gray|heather grey|athletic heather|silver|lavender|peach|beyaz|krem|bej)\b/i;

export function garmentTheme(color: string | null | undefined): "light" | "dark" | null {
  if (!color) return null;
  if (DARK.test(color)) return "dark";
  if (LIGHT.test(color)) return "light";
  return null;
}

const THEME_LABEL = { light: "Açık zemin", dark: "Koyu zemin" } as const;

function CopyMini({ text, title }: { text: string; title: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={(e) => {
        e.preventDefault(); e.stopPropagation();
        navigator.clipboard.writeText(text).then(() => { setOk(true); setTimeout(() => setOk(false), 1200); });
      }}
      title={ok ? "Kopyalandı" : title}
      className="rounded px-1 text-[10px] text-gray-400 hover:bg-gray-200 hover:text-gray-700"
    >
      {ok ? "✓" : "kopyala"}
    </button>
  );
}

/** Capraz kaynakta <a download> calismaz; dosya cekilip yerelde kaydedilir. */
function DownloadButton({ url, filename, label, recommended, designId, which, big }: { url: string; filename: string; label: string; recommended: boolean; designId?: string; which?: "main" | "variant"; big?: boolean }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      // Tedarikci dosyasi: kirpilmis + 300 DPI 11 inc (bos kenar baskiyi kucultmesin)
      if (designId) { await downloadPrintFile(designId, { which, filename, size: big ? "big" : "standard" }); return; }
      const res = await fetch(absUrl(url));
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const ext = (blob.type.split("/")[1] || "png").replace("jpeg", "jpg");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${filename}.${ext}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch {
      window.open(absUrl(url), "_blank", "noopener"); // indirilemezse yeni sekmede ac
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      title={recommended ? "Bu kumaş rengi için basılacak dosya" : "İndir"}
      className={clsx(
        "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px]",
        recommended ? "border-emerald-300 bg-emerald-50 font-medium text-emerald-800 hover:bg-emerald-100" : "border-gray-200 text-gray-600 hover:bg-gray-50",
      )}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
      {label}
      {recommended && <Star className="h-2.5 w-2.5 fill-emerald-500 text-emerald-500" />}
    </button>
  );
}

function DesignRow({ design, color, bigPrint }: { design: LinkedDesign; color: string | null; bigPrint?: boolean }) {
  const main = design.editedUrl || design.originalUrl;
  const img = absUrl(design.thumbnailUrl || main);
  const want = garmentTheme(color);
  const files: Array<{ url: string; theme: "light" | "dark" | null }> = [];
  if (main) files.push({ url: main, theme: design.theme ?? null });
  if (design.variantUrl) files.push({ url: design.variantUrl, theme: design.variantTheme ?? null });
  const hasBoth = files.length > 1;
  const base = (design.sku || design.name || "tasarim").replace(/[^\w.-]+/g, "_");
  // Baski onizlemesi: bu tisort rengine basilacak dosya, tisort renginde zeminde
  const printUrl = absUrl((hasBoth && want ? files.find((f) => f.theme === want)?.url : null) ?? main);
  const bg = garmentHex(color);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {printUrl && (
        <span className="group/print relative" title={`Baskı önizlemesi${color ? ` · ${color} tişört` : ""}`}>
          <span className="block h-9 w-9 overflow-hidden rounded-md border border-gray-200" style={{ background: bg }}>
            <img src={printUrl} alt="" className="h-full w-full object-contain p-0.5" />
          </span>
          <span className="pointer-events-none absolute bottom-full left-0 z-40 mb-2 hidden w-64 rounded-xl border border-gray-200 bg-white p-2 shadow-2xl group-hover/print:block">
            <span className="block rounded-lg p-3" style={{ background: bg }}>
              <img src={printUrl} alt="" className="max-h-72 w-full object-contain" />
            </span>
            <span className="mt-1.5 block text-center text-[11px] text-gray-500">Baskı önizlemesi{color ? ` · ${color} tişört` : ""}</span>
          </span>
        </span>
      )}
      <span className="group/design relative">
        <Link
          to={`/designs?search=${encodeURIComponent(design.sku || design.name)}`}
          className="inline-flex items-center gap-1 rounded border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[11px] text-indigo-700 hover:bg-indigo-100"
          title={`Tasarım: ${design.name}`}
        >
          {img ? <img src={img} alt="" className="h-4 w-4 rounded object-cover" /> : <Palette className="h-3 w-3" />}
          {design.name}
        </Link>
        {img && (
          <span className="pointer-events-none absolute bottom-full left-0 z-40 mb-2 hidden group-hover/design:block">
            <img src={img} alt="" className="max-h-72 w-64 rounded-xl border border-gray-200 bg-white object-contain p-1 shadow-2xl" />
          </span>
        )}
      </span>
      {files.map((f, i) => (
        <DownloadButton
          key={f.url}
          url={f.url}
          designId={design.id}
          which={i === 0 ? "main" : "variant"}
          big={bigPrint}
          filename={f.theme ? `${base}-${f.theme === "dark" ? "koyu" : "acik"}` : base}
          label={f.theme ? THEME_LABEL[f.theme] : hasBoth ? (i === 0 ? "Ana dosya" : "İkinci dosya") : "İndir"}
          recommended={hasBoth && !!want && f.theme === want}
        />
      ))}
    </div>
  );
}

export default function OrderDesignFiles({
  design, alts, sku, color, supplier, bigPrint,
}: {
  /** Big print listeden gelen siparis: 16 inc baski dosyasi */
  bigPrint?: boolean;
  design?: LinkedDesign | null;
  alts?: LinkedDesign[];
  sku?: string | null;
  color: string | null;
  supplier?: SupplierInfo | null;
}) {
  return (
    <div className="mt-2 space-y-1.5">
      {design ? (
        <>
          {bigPrint && (
            <span className="inline-flex rounded bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold text-white"
              title="Big print listeden gelen sipariş: indirilen dosyalar Rexven Tam Boy kutusuna (13 × 17,5 inç) hazırlanır">
              Big print · 13×17,5″
            </span>
          )}
          <DesignRow design={design} color={color} bigPrint={bigPrint} />
          {(alts ?? []).map((d) => <DesignRow key={d.id} design={d} color={color} bigPrint={bigPrint} />)}
          <OrderSampleMockup designs={[design, ...(alts ?? [])]} sku={sku ?? design.sku} />
        </>
      ) : sku ? (
        <span className="flex flex-wrap items-center gap-1.5">
        <Link
          to={`/designs?search=${encodeURIComponent(sku)}`}
          className="inline-flex items-center gap-1 rounded border border-dashed border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-400 hover:bg-gray-50"
        >
          <Palette className="h-3 w-3" /> Tasarım eşleşmedi
        </Link>
        {/* Eslesmeyen sipariste de disaridan yuklenen tasarimla ornek */}
        <OrderSampleMockup designs={[]} sku={sku} />
        </span>
      ) : null}

      {supplier && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="inline-flex items-center gap-1 rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-orange-800">
            <Factory className="h-3 w-3" />
            <span className="capitalize">{supplier.supplier}</span>
            {supplier.sku && (
              <>
                <span className="font-mono font-semibold">{supplier.sku}</span>
                <CopyMini text={supplier.sku} title={`${supplier.supplier} SKU kopyala`} />
              </>
            )}
          </span>
          {supplier.variantId && (
            <span className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-700">
              Varyant {supplier.variantId}
              <CopyMini text={supplier.variantId} title="Varyant kimliğini kopyala" />
            </span>
          )}
          {supplier.productName && (
            <span className="max-w-[16rem] truncate text-gray-500" title={supplier.productName}>
              {supplier.productName}{supplier.size ? ` · ${supplier.size}` : ""}
            </span>
          )}
          {supplier.url && (
            <a
              href={supplier.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1.5 py-0.5 text-gray-600 hover:bg-gray-50"
            >
              Ürün sayfası <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
